import { StoryMapState, StoryLocation } from '@app/components/Risk/types'
import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { Map } from 'mapbox-gl'

/**
 * Extract the current map state for use in a story step
 */
export interface CapturedMapState {
  location: StoryLocation
  basemap: string
  elements: string[]
  scenarios: string[]
  contexts: string[]
  regions: string[]
}

/**
 * Capture the current map view state (camera position)
 */
export const captureMapLocation = (map: Map | null): StoryLocation | null => {
  if (!map) return null

  const center = map.getCenter()
  const zoom = map.getZoom()
  const pitch = map.getPitch()
  const bearing = map.getBearing()

  return {
    latitude: center.lat,
    longitude: center.lng,
    zoom,
    pitch: pitch || undefined,
    bearing: bearing || undefined,
  }
}

/**
 * Capture the current layers from the map
 */
export const captureMapLayers = (
  layers: MapLayer[],
): { elements: string[]; scenarios: string[]; contexts: string[] } => {
  const elements: string[] = []
  const scenarios: string[] = []
  const contexts: string[] = []

  layers.forEach((layer) => {
    if (!layer.visible) return

    switch (layer.layerType) {
      case 'asset':
        elements.push(layer.type || layer.id)
        break
      case 'hazard':
        scenarios.push(layer.assetTag || layer.id)
        break
      case 'information':
        contexts.push(layer.id)
        break
    }
  })

  return { elements, scenarios, contexts }
}

/**
 * Convert basemap style URL to story basemap name
 */
export const basemapStyleToName = (styleUrl: string): string => {
  const lcStyleUrl = styleUrl.toLocaleLowerCase()
  if (lcStyleUrl.includes('satellite')) return 'Satellite'
  if (lcStyleUrl.includes('outdoors') || lcStyleUrl.includes('contours')) return 'Contours'
  return 'Street'
}

/**
 * Convert story basemap name to style URL
 */
export const basemapNameToStyle = (name: string): string => {
  switch (name) {
    case 'Satellite':
      return 'mapbox://styles/mapbox/satellite-streets-v12'
    case 'Contours':
      return 'mapbox://styles/mapbox/outdoors-v12'
    case 'Street':
    default:
      return 'mapbox://styles/mapbox/light-v11'
  }
}

/**
 * Capture the complete map state for a story step
 */
export const captureCompleteMapState = (
  map: Map | null,
  layers: MapLayer[],
  basemap: string,
  regions: string[] = [],
): CapturedMapState | null => {
  const location = captureMapLocation(map)
  if (!location) return null

  const { elements, scenarios, contexts } = captureMapLayers(layers)

  return {
    location,
    basemap: basemapStyleToName(basemap),
    elements,
    scenarios,
    contexts,
    regions,
  }
}

/**
 * Convert captured map state to story map state format
 */
export const capturedStateToStoryMapState = (captured: CapturedMapState): StoryMapState => {
  return {
    location: captured.location,
    basemap: captured.basemap,
    elements: captured.elements.length > 0 ? captured.elements : undefined,
    scenarios: captured.scenarios.length > 0 ? captured.scenarios : undefined,
    contexts: captured.contexts.length > 0 ? captured.contexts : undefined,
    regions: captured.regions.length > 0 ? captured.regions : undefined,
  }
}

/**
 * Check if two map states are equivalent
 */
export const areMapStatesEqual = (
  a: StoryMapState | undefined,
  b: StoryMapState | undefined,
): boolean => {
  if (!a && !b) return true
  if (!a || !b) return false

  const arraysEqual = (arr1?: string[], arr2?: string[]) => {
    if (!arr1 && !arr2) return true
    if (!arr1 || !arr2) return false
    if (arr1.length !== arr2.length) return false
    return arr1.every((v, i) => v === arr2[i])
  }

  return (
    a.basemap === b.basemap &&
    arraysEqual(a.elements, b.elements) &&
    arraysEqual(a.scenarios, b.scenarios) &&
    arraysEqual(a.contexts, b.contexts) &&
    arraysEqual(a.regions, b.regions) &&
    a.location?.latitude === b.location?.latitude &&
    a.location?.longitude === b.location?.longitude &&
    a.location?.zoom === b.location?.zoom
  )
}
